import * as React from 'react'
import { useDispatch } from 'react-redux'
import { setIsStored } from '../../../redux/slices/Alerts'
import {
  Box,
  Paper,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Divider,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import { datasetFormik } from './datasetFormik'
import { Formik, Form, useFormikContext } from 'formik'
import Textfield from '../../FormsUI/Textfield'
import Select from '../../FormsUI/Select'
import SvgInputField from './SvgInputField'
import Modal from '../../FormCreationModal'
import { createCountry, updateCountry } from '../../../services/countryService'

const STEPS = [
  { label: 'Información Básica', description: 'Datos del País' },
]

const gridStyle = {
  mt: 0,
  width: '100%',
  ml: 0,
  textAlignLast: 'right',
  justifyContent: 'space-evenly',
  pr: 2,
}

const FormContent = ({ formik, onCancel, onSubmit, initialValues }) => {
  const { errors, touched } = useFormikContext()

  const otherPropsControl = {
    InputProps: { readOnly: false },
    InputLabelProps: { shrink: true },
    variant: 'outlined',
  }

  const renderField = (field, index) => {
    if (field.name === 'icono_svg') {
      return (
        <Grid key={`field-${index}`} item xs={field.x} sx={{ textAlignLast: 'left' }}>
          <SvgInputField name="icono_svg" />
        </Grid>
      )
    }

    const child = !field.options ? (
      <Textfield
        {...otherPropsControl}
        {...field}
        type={field.type || 'text'}
      />
    ) : (
      <Select key={`field-${index}`} {...field} {...otherPropsControl} />
    )

    return (
      <Grid
        key={`field-${index}`}
        item
        xs={field.x}
        sx={{ textAlignLast: 'center' }}
      >
        {child}
      </Grid>
    )
  }

  return (
    <Box>
      <Stepper activeStep={0} sx={{ pt: 2, pb: 3 }}>
        {STEPS.map(step => (
          <Step key={step.label}>
            <StepLabel>
              <Typography variant="body2">{step.label}</Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Divider />

      <Box sx={{ py: 2, px: 2, backgroundColor: 'grey.50' }}>
        <Typography variant="subtitle2" color="text.secondary">
          Paso 1: {STEPS[0].description}
        </Typography>
      </Box>

      <Divider />

      <Paper
        style={{
          overflow: 'auto',
          borderRadius: 0,
          maxHeight: '50vh',
          minHeight: 300,
        }}
        sx={{ p: 2 }}
      >
        <Grid container spacing={2} sx={{ ...gridStyle }}>
          {formik.STEP1_FIELDS.map((field, index) => renderField(field, index))}
        </Grid>

        {(Object.keys(errors).length > 0 && Object.keys(touched).length > 0) && (
          <Typography variant="caption" color="error" sx={{ mt: 2, display: 'block' }}>
            Revisa los campos requeridos.
          </Typography>
        )}
      </Paper>

      <Divider />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}>
        <Button onClick={onCancel} color="inherit">
          Cancelar
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={onSubmit}
          startIcon={<CheckIcon />}
        >
          {initialValues ? 'Actualizar' : 'Crear'}
        </Button>
      </Box>
    </Box>
  )
}

const CountryFormModal = (props, ref) => {
  const dispatch = useDispatch()

  const { initialValues, onClose, onSuccess } = props
  const [formik, setFormik] = React.useState(null)
  const [open, setOpen] = React.useState(false)
  const formikRef = React.useRef(null)

  const handleCancelAction = () => {
    setOpen(false)
    if (onClose) onClose()
  }

  const handleSubmitForm = async () => {
    if (!formikRef.current) return
    formikRef.current.submitForm()
  }

  const handleAddAction = async values => {
    let resultSave = { stored: true }

    const data = {
      nombre: values.nombre,
      slug: values.slug || undefined,
      codigo_iso2: values.codigo_iso2 || undefined,
      codigo_iso3: values.codigo_iso3 || undefined,
      label_n1: values.label_n1,
      label_n2: values.label_n2,
      label_n3: values.label_n3,
      habilitado: Boolean(parseInt(values.habilitado, 10)),
      icono_svg: values.icono_svg || null,
      center_lat: values.center_lat !== '' && values.center_lat != null ? parseFloat(values.center_lat) : null,
      center_lng: values.center_lng !== '' && values.center_lng != null ? parseFloat(values.center_lng) : null,
      zoom: values.zoom !== '' && values.zoom != null ? parseInt(values.zoom, 10) : null,
    }

    if (values.orden !== '' && values.orden != null) {
      data.orden = parseInt(values.orden, 10)
    }

    let response
    if (initialValues?.id) {
      response = await updateCountry(initialValues.id, data)
    } else {
      response = await createCountry(data)
    }

    if (!response.success) {
      resultSave = { stored: false, message: response?.message || 'Error al guardar' }
    } else if (onSuccess) {
      await onSuccess()
    }

    return resultSave
  }

  React.useImperativeHandle(ref, () => ({ openModal: () => setOpen(true) }))

  React.useEffect(() => {
    if (props.open !== undefined) setOpen(props.open)
  }, [props.open])

  React.useEffect(() => {
    if (!open) return
    datasetFormik().then(variables => {
      if (initialValues) {
        variables.INITIAL_FORMIK_STATE = {
          ...variables.INITIAL_FORMIK_STATE,
          nombre: initialValues.nombre || '',
          slug: initialValues.slug || '',
          codigo_iso2: initialValues.codigo_iso2 || '',
          codigo_iso3: initialValues.codigo_iso3 || '',
          label_n1: initialValues.label_n1 || '',
          label_n2: initialValues.label_n2 || '',
          label_n3: initialValues.label_n3 || '',
          habilitado: initialValues.habilitado === true ? 1 : 0,
          orden: initialValues.orden ?? '',
          icono_svg: initialValues.icono_svg || '',
          center_lat: initialValues.center ? initialValues.center[1] : '',
          center_lng: initialValues.center ? initialValues.center[0] : '',
          zoom: initialValues.zoom ?? 6,
        }
      }
      setFormik(variables)
    })
    return () => {
      setFormik(null)
    }
  }, [open, initialValues])

  if (!Boolean(formik)) return null

  return (
    <div>
      <Modal
        open={open}
        title={initialValues ? 'Editar País' : 'Crear un País'}
        description=""
      >
        <Box>
          <Formik
            innerRef={formikRef}
            initialValues={{ ...formik?.INITIAL_FORMIK_STATE }}
            validationSchema={formik?.FORM_VALIDATION}
            onSubmit={async values => {
              const itsStored = await handleAddAction(values)
              handleCancelAction()
              dispatch(setIsStored(itsStored))
            }}
          >
            <Form>
              <FormContent
                formik={formik}
                onCancel={handleCancelAction}
                onSubmit={handleSubmitForm}
                initialValues={initialValues}
              />
            </Form>
          </Formik>
        </Box>
      </Modal>
    </div>
  )
}

export default React.forwardRef(CountryFormModal)
